import { forwardRef, useImperativeHandle, useRef, useCallback } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Box, Typography } from "@mui/material";

const RECAPTCHA_SITE_KEY = "6Lfz-F0sAAAAAI88XTkiwOTEcYUKkW3Z9A7vz1TL";

/**
 * ReCaptcha Component
 * @param {Object} props
 * @param {Function} props.onVerify - Callback when captcha is verified (receives token)
 * @param {Function} props.onExpire - Callback when captcha expires
 * @param {Function} props.onError - Callback when captcha errors
 * @param {string} props.error - Error message to display
 */
const ReCaptcha = forwardRef(({ onVerify, onExpire, onError, error }, ref) => {
  const recaptchaRef = useRef(null);

  // Expose reset method to parent
  useImperativeHandle(ref, () => ({
    reset: () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    },
    getValue: () => {
      if (recaptchaRef.current) {
        return recaptchaRef.current.getValue();
      }
      return null;
    },
  }));

  const handleChange = useCallback(
    (token) => {
      if (token) {
        onVerify?.(token);
      }
    },
    [onVerify]
  );

  const handleExpired = useCallback(() => {
    onExpire?.();
  }, [onExpire]);

  const handleError = useCallback(() => {
    onError?.();
  }, [onError]);

  return (
    <Box sx={{ my: 2 }}>
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={RECAPTCHA_SITE_KEY}
        onChange={handleChange}
        onExpired={handleExpired}
        onErrored={handleError}
      />
      {error && (
        <Typography
          variant="caption"
          sx={{
            color: "#d32f2f",
            display: "block",
            mt: 1,
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
});

ReCaptcha.displayName = "ReCaptcha";

export default ReCaptcha;
